/**
 * ProtectedRoute Component
 *
 * Higher-order component that protects admin routes from unauthorized access.
 * Checks authentication state and redirects unauthenticated users to login.
 *
 * Features:
 * - Checks authentication state using AdminContext
 * - Shows loading indicator during auth initialization
 * - Redirects to /admin if not authenticated
 * - Renders protected content if authenticated
 * - Uses replace navigation to prevent back-button issues
 *
 * Authentication Flow:
 * 1. Component mounts and checks isInitializing
 * 2. If initializing: Show loading indicator
 * 3. If not authenticated: Navigate to /admin (login page)
 * 4. If authenticated: Render children (protected content)
 *
 * Usage:
 * ```tsx
 * <Route path="/admin/dashboard" element={
 *   <ProtectedRoute>
 *     <AdminDashboard />
 *   </ProtectedRoute>
 * } />
 * ```
 */

import { useAdmin } from '@context/AdminContext';
import type { ReactNode } from 'react';
import React from 'react';
import { Navigate } from 'react-router-dom';

/**
 * ProtectedRoute Props
 */
type ProtectedRouteProps = {
  /**
   * The content to render if user is authenticated
   */
  readonly children: ReactNode;
};

/**
 * ProtectedRoute Component
 *
 * Wraps admin routes to ensure only authenticated users can access them.
 * @param root0
 * @param root0.children
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAdmin();

  // Show loading state while checking authentication
  // This prevents flashing login page during initial auth check
  if (isInitializing) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          color: '#ffffff',
          fontSize: '18px',
        }}
      >
        <div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login page
  // Use 'replace' to prevent adding to browser history
  // This prevents users from going back to protected pages after logout
  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
